import classNames from "classnames";
import Input from "components/DesignSystem/Input/Input";
import { BareInput } from "components/DesignSystem/Input/Input";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { Button } from "components/DesignSystem/Button/Button";
import {
  BaseFileInput,
  DropFile,
} from "components/DesignSystem/BaseFileInput/BaseFileInput";
import { Tag } from "components/DesignSystem/Tag/Tag";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { FormikForm } from "components/FormikForm/FormikForm";
import { TrashSimple } from "components/icons/TrashSimple";
import { CaretDown } from "components/icons/CaretDown";
import {
  FORM_INSTANCE_STATUS,
  FormInstanceStatus,
  QUESTION_TYPE,
  IncorporationFormSource,
  INCORPORATION_FORM_SOURCE,
  PENDING_ANSWER_TYPE,
} from "constants/incorporation";
import { useToast } from "hooks/useToast";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useModal } from "hooks/useModal";
import { debounce } from "utils/debouncing";
import { formatDate } from "utils/formatDate";
import { Question } from "store/apis/incorporationFormBuilder";
import {
  useUpdateFormInstanceNodeMutation,
  useUploadQuestionDocumentMutation,
} from "store/apis/incorporationFormBuilder";
import { BackendError } from "types/utils/error";
import { useEffect, useMemo, useState } from "react";
import { FilePreviewTag } from "components/FilePreviewTag/FilePreviewTag";
import { Preview } from "components/PreviewModal";
import { IncorporationQuestionOptionsList } from "./IncorporationQuestionOptionsList";
import { AdminAnswerWarningModal } from "./AdminAnswerWarningModal";
import { IncorporationLockedFieldTooltip } from "./IncorporationLockedFieldTooltip";
import {
  useDeleteFormTreeNodeMutation,
  useUpdateFormTreeNodeMutation,
} from "./useFormTreeNodeMutations";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { isQuestionAnswered } from "util/incorporationFormProgress";
import { Check } from "components/icons/Check";
import { IncorporationQuestionSaveStatus } from "./IncorporationQuestionSaveStatus";

const QUESTION_TYPE_OPTIONS = [
  { label: "Text input", value: String(QUESTION_TYPE.TEXT_INPUT) },
  { label: "Single select", value: String(QUESTION_TYPE.SINGLE_SELECT) },
  { label: "Document upload", value: String(QUESTION_TYPE.MULTI_DOC_UPLOAD) },
];

const ADMIN_STATUS_OPTIONS = [
  { label: "Not started", value: String(FORM_INSTANCE_STATUS.NOT_STARTED) },
  { label: "In progress", value: String(FORM_INSTANCE_STATUS.IN_PROGRESS) },
  { label: "Completed", value: String(FORM_INSTANCE_STATUS.COMPLETED) },
];

const ADMIN_STATUS_TAG_TYPE: Record<
  FormInstanceStatus,
  "gray" | "orange" | "green"
> = {
  [FORM_INSTANCE_STATUS.NOT_STARTED]: "gray",
  [FORM_INSTANCE_STATUS.IN_PROGRESS]: "orange",
  [FORM_INSTANCE_STATUS.COMPLETED]: "green",
};

const getAdminStatusLabel = (status: FormInstanceStatus) =>
  ADMIN_STATUS_OPTIONS.find((option) => option.value === String(status))
    ?.label ?? ADMIN_STATUS_OPTIONS[0].label;

export const IncorporationAdminStatusChip = ({
  status,
}: {
  status: FormInstanceStatus;
}) => {
  return (
    <Tag
      tagType={ADMIN_STATUS_TAG_TYPE[status]}
      rounded
      size="small"
      icon={false}
    >
      {getAdminStatusLabel(status)}
    </Tag>
  );
};

export const IncorporationAdminStatusDot = ({
  status,
}: {
  status: FormInstanceStatus;
}) => (
  <Tag tagType={ADMIN_STATUS_TAG_TYPE[status]} size="small" transparent>
    {getAdminStatusLabel(status)}
  </Tag>
);

const IncorporationDeleteFieldButton = ({
  source,
  treeId,
  questionId,
  disabled = false,
}: {
  source: IncorporationFormSource;
  treeId: string;
  questionId: string;
  disabled?: boolean;
}) => {
  const { alertToast } = useToast();
  const [deleteQuestion, { isLoading: isDeleting }] =
    useDeleteFormTreeNodeMutation({ source });

  const handleDelete = async () => {
    try {
      await deleteQuestion({
        treeId,
        body: { question: { node_id: questionId } },
      }).unwrap();
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  return (
    <Button
      type="button"
      customType="link_neutral"
      leftIcon={<TrashSimple />}
      isLoading={isDeleting}
      disabled={isDeleting || disabled}
      onClick={handleDelete}
    >
      Delete field
    </Button>
  );
};

const IncorporationQuestionBuilderFields = ({
  question,
  source,
  treeId,
  isDisabled = false,
}: {
  question: Question;
  source: IncorporationFormSource;
  treeId: string;
  isDisabled?: boolean;
}) => {
  const { alertToast } = useToast();
  const [updateQuestion] = useUpdateFormTreeNodeMutation({ source });

  const onActionError = (err: unknown) => {
    alertToast({
      message:
        (err as BackendError)?.data?.error?.message ?? "Something went wrong",
    });
  };

  const saveLabel = async (label: string) => {
    try {
      await updateQuestion({
        treeId,
        body: { question: { node_id: question.uuid, label } },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const saveHelperText = async (helperText: string) => {
    try {
      await updateQuestion({
        treeId,
        body: {
          question: {
            node_id: question.uuid,
            config: { ...question.config, helper_text: helperText },
          },
        },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const onQuestionTypeChange = async (value: string) => {
    try {
      await updateQuestion({
        treeId,
        body: {
          question: {
            node_id: question.uuid,
            question_type: Number(value) as Question["question_type"],
          },
        },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const debouncedSaveLabel = useMemo(
    () => debounce(saveLabel),
    [question.uuid]
  );
  const debouncedSaveHelperText = useMemo(
    () => debounce(saveHelperText),
    [question.uuid, question.config]
  );

  const isReadOnlyQuestion = question.question_type === QUESTION_TYPE.READ_ONLY;

  return (
    <IncorporationLockedFieldTooltip isLocked={isDisabled}>
      <div className="t-flex t-gap-3 sm:t-flex-col sm:t-gap-4">
        <div className="t-flex t-w-40 t-shrink-0 t-flex-col t-justify-between t-gap-3 sm:t-w-full sm:t-flex-row sm:t-items-center">
          <div className="t-flex t-flex-col t-gap-1 sm:t-w-40">
            <span className="t-invisible t-text-body-sm sm:t-hidden">
              &nbsp;
            </span>
            {isReadOnlyQuestion ? (
              <Tag tagType="gray" icon={false}>
                Read-only field
              </Tag>
            ) : (
              <Combobox
                options={QUESTION_TYPE_OPTIONS}
                value={QUESTION_TYPE_OPTIONS.find(
                  (option) => option.value === String(question.question_type)
                )}
                onChange={(option) =>
                  option &&
                  "value" in option &&
                  onQuestionTypeChange(option.value)
                }
                menuPortalTarget={document.body}
                isClearable={false}
                isDisabled={isDisabled}
                size="small"
              />
            )}
          </div>
          <IncorporationDeleteFieldButton
            source={source}
            treeId={treeId}
            questionId={question.uuid}
            disabled={isDisabled}
          />
        </div>

        <div className="t-flex t-min-w-0 t-flex-1 t-flex-col t-gap-4">
          <FormikForm
            initialValues={{
              label: question.label,
              helperText: question.config?.helper_text || "",
            }}
            onSubmit={() => {}}
          >
            <div className="t-flex t-flex-col t-gap-4">
              <Input
                withForm
                name="label"
                label="Field name"
                required
                disabled={isDisabled}
                customSize="small"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  debouncedSaveLabel(e.target.value)
                }
              />
              {question.question_type !== QUESTION_TYPE.SINGLE_SELECT && (
                <Input
                  withForm
                  name="helperText"
                  label="Description / helper text"
                  disabled={isDisabled}
                  customSize="small"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    debouncedSaveHelperText(e.target.value)
                  }
                />
              )}
            </div>
          </FormikForm>

          {question.question_type === QUESTION_TYPE.SINGLE_SELECT && (
            <IncorporationQuestionOptionsList
              questionId={question.uuid}
              options={question.options}
              source={source}
              treeId={treeId}
              isDisabled={isDisabled}
            />
          )}
        </div>
      </div>
    </IncorporationLockedFieldTooltip>
  );
};

const IncorporationQuestionViewOnlyField = ({
  question,
}: {
  question: Question;
}) => {
  if (question.question_type === QUESTION_TYPE.SINGLE_SELECT) {
    return (
      <RadioGroup.Root disabled>
        <RadioGroup.Content>
          {question.options.map((option) => (
            <RadioGroup.Item key={option.uuid} value={option.uuid}>
              {option.label}
            </RadioGroup.Item>
          ))}
        </RadioGroup.Content>
      </RadioGroup.Root>
    );
  }

  if (question.question_type === QUESTION_TYPE.MULTI_DOC_UPLOAD) {
    return <BaseFileInput multiple disabled />;
  }

  if (question.question_type === QUESTION_TYPE.READ_ONLY) {
    const answerText =
      question.answer && "text" in question.answer
        ? question.answer.text
        : null;
    const documentAnswers =
      question.answer && "documents" in question.answer
        ? question.answer.documents
        : [];

    if (!answerText && documentAnswers?.length <= 0) return null;
    return (
      <div
        className={classNames(
          "t-flex t-flex-col t-gap-3 t-border-0 t-border-b t-border-solid t-border-neutral-10 t-pb-3"
        )}
      >
        <div className="t-flex t-flex-col t-gap-2 t-rounded t-border-0 t-border-l-2 t-border-solid t-border-l-neutral-20 t-bg-surface-lighter-grey t-p-3">
          {documentAnswers.length > 0 && (
            <div className="t-flex t-flex-col t-gap-2">
              {documentAnswers.map((document) => (
                <FilePreviewTag
                  key={document.document_answer_id}
                  file={document}
                  block
                  size="small"
                  className="!t-border-0 !t-p-0"
                />
              ))}
            </div>
          )}
          {answerText && (
            <span className="t-text-body-sm t-text-text-30">{answerText}</span>
          )}
          {question.answered_by && question.answered_at && (
            <span className="t-text-body-sm-italic t-italic t-text-text-30">
              Updated by {question.answered_by} on{" "}
              {formatDate(question.answered_at)}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <BareInput placeholder="Placeholder text" disabled className="t-w-full" />
  );
};

type PendingAnswer =
  | { type: typeof PENDING_ANSWER_TYPE.TEXT; text: string }
  | { type: typeof PENDING_ANSWER_TYPE.OPTION; optionInstanceId: string }
  | { type: typeof PENDING_ANSWER_TYPE.UPLOAD; files: File[] }
  | { type: typeof PENDING_ANSWER_TYPE.DELETE; documentAnswerId: string };

const IncorporationQuestionAnswerField = ({
  question,
  treeId,
  groupId,
  isAdmin = false,
  onSavingChange,
}: {
  question: Question;
  treeId: string;
  groupId?: string;
  isAdmin?: boolean;
  onSavingChange?: (isSaving: boolean) => void;
}) => {
  const { alertToast } = useToast();
  const [answerQuestion, { isLoading: isSavingAnswer }] =
    useUpdateFormInstanceNodeMutation();
  const [uploadDocument, { isLoading: isUploading }] =
    useUploadQuestionDocumentMutation();
  const [isTextFieldFocused, setIsTextFieldFocused] = useState(false);
  const [pendingAnswer, setPendingAnswer] = useState<PendingAnswer | null>(
    null
  );
  const [previewId, setPreviewId] = useState<string | null>(null);
  const previewModal = useModal();

  useEffect(() => {
    onSavingChange?.(isSavingAnswer || isUploading);
  }, [isSavingAnswer, isUploading, onSavingChange]);

  const onActionError = (err: unknown) => {
    alertToast({
      message:
        (err as BackendError)?.data?.error?.message ?? "Something went wrong",
    });
  };

  const onTextFieldFocus = () => setIsTextFieldFocused(true);

  const saveTextAnswer = async (text: string) => {
    try {
      await answerQuestion({
        treeId,
        body: { question: { node_id: question.uuid, answer: { text } } },
      }).unwrap();
      setIsTextFieldFocused(false);
    } catch (err) {
      onActionError(err);
    }
  };

  const saveOptionAnswer = async (optionInstanceId: string) => {
    try {
      await answerQuestion({
        treeId,
        body: {
          question: {
            node_id: question.uuid,
            answer: { option_instance_id: optionInstanceId },
          },
        },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const uploadDocuments = async (files: File[]) => {
    if (!groupId) {
      return;
    }

    try {
      await uploadDocument({
        groupId,
        questionInstanceId: question.uuid,
        files,
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const deleteDocument = async (documentAnswerId: string) => {
    try {
      await answerQuestion({
        treeId,
        body: {
          question: {
            node_id: question.uuid,
            answer: { remove_document_answer_id: documentAnswerId },
          },
        },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const onCloseWarningModal = () => setPendingAnswer(null);

  const onConfirmPendingAnswer = async () => {
    if (!pendingAnswer) {
      return;
    }

    if (pendingAnswer.type === PENDING_ANSWER_TYPE.TEXT) {
      await saveTextAnswer(pendingAnswer.text);
    } else if (pendingAnswer.type === PENDING_ANSWER_TYPE.OPTION) {
      await saveOptionAnswer(pendingAnswer.optionInstanceId);
    } else if (pendingAnswer.type === PENDING_ANSWER_TYPE.UPLOAD) {
      await uploadDocuments(pendingAnswer.files);
    } else {
      await deleteDocument(pendingAnswer.documentAnswerId);
    }

    setPendingAnswer(null);
  };

  const saveTextAnswerAction = (text: string) => {
    if (isAdmin) {
      setPendingAnswer({ type: PENDING_ANSWER_TYPE.TEXT, text });
      return;
    }

    saveTextAnswer(text);
  };

  const onSelectAnswerChange = (optionInstanceId: string) => {
    if (isAdmin) {
      setPendingAnswer({ type: PENDING_ANSWER_TYPE.OPTION, optionInstanceId });
      return;
    }

    saveOptionAnswer(optionInstanceId);
  };

  const onDropDocuments = (files: File[]) => {
    if (isAdmin) {
      setPendingAnswer({ type: PENDING_ANSWER_TYPE.UPLOAD, files });
      return;
    }

    uploadDocuments(files);
  };

  const onDeleteDocument = (documentAnswerId: string) => {
    if (isAdmin) {
      setPendingAnswer({ type: PENDING_ANSWER_TYPE.DELETE, documentAnswerId });
      return;
    }

    deleteDocument(documentAnswerId);
  };

  const currentAnswerText =
    question.answer && "text" in question.answer
      ? question.answer.text ?? ""
      : "";
  const currentSelectedOptionId =
    question.answer && "option_instance_id" in question.answer
      ? question.answer.option_instance_id
      : undefined;
  const documentAnswers =
    question.answer && "documents" in question.answer
      ? question.answer.documents
      : [];

  if (question.question_type === QUESTION_TYPE.SINGLE_SELECT) {
    return (
      <>
        <RadioGroup.Root
          value={currentSelectedOptionId}
          onValueChange={onSelectAnswerChange}
        >
          <RadioGroup.Content>
            {question.options.map((option) => (
              <RadioGroup.Item key={option.uuid} value={option.uuid}>
                {option.label}
              </RadioGroup.Item>
            ))}
          </RadioGroup.Content>
        </RadioGroup.Root>
        <AdminAnswerWarningModal
          isOpen={Boolean(pendingAnswer)}
          close={onCloseWarningModal}
          onGoBack={onCloseWarningModal}
          onConfirm={onConfirmPendingAnswer}
          isLoading={isSavingAnswer}
        />
      </>
    );
  }

  if (question.question_type === QUESTION_TYPE.MULTI_DOC_UPLOAD) {
    return (
      <>
        <BaseFileInput
          multiple
          disabled={!groupId}
          isUploading={isUploading}
          onDrop={onDropDocuments}
          files={documentAnswers}
          onFileClick={({ uuid }) => {
            setPreviewId(uuid);
            previewModal.open();
          }}
          onDelete={(_, index) =>
            onDeleteDocument(documentAnswers[index].document_answer_id)
          }
        />
        <Preview
          showModal={previewModal.isOpen}
          closeModal={previewModal.close}
          groupId={groupId}
          previewId={previewId}
        />
        <AdminAnswerWarningModal
          isOpen={Boolean(pendingAnswer)}
          close={onCloseWarningModal}
          onGoBack={onCloseWarningModal}
          onConfirm={onConfirmPendingAnswer}
          isLoading={isSavingAnswer || isUploading}
        />
      </>
    );
  }

  const hasSavedAnswer = Boolean(currentAnswerText);
  const saveButtonLabel = hasSavedAnswer ? "Save changes" : "Save";
  const cancelButtonLabel = hasSavedAnswer ? "Discard" : "Cancel";

  const onSaveTextAnswer = async ({ answer }: { answer: string }) => {
    saveTextAnswerAction(answer);
  };

  return (
    <FormikForm
      initialValues={{ answer: currentAnswerText }}
      enableReinitialize
      onSubmit={onSaveTextAnswer}
    >
      {({ resetForm, submitForm }) => {
        const onCancel = () => {
          resetForm();
          setIsTextFieldFocused(false);
        };

        const onGoBack = () => {
          resetForm();
          setIsTextFieldFocused(false);
          onCloseWarningModal();
        };

        return (
          <div className="t-flex t-items-start t-gap-2 sm:t-flex-col sm:t-items-stretch">
            <TextArea
              name="answer"
              label=""
              rows={1}
              block
              onFocus={onTextFieldFocus}
              size="small"
            />
            {isTextFieldFocused && (
              <div className="t-flex t-shrink-0 t-gap-2 sm:t-self-end">
                <Button
                  type="button"
                  customType="primary"
                  size="small"
                  isLoading={isSavingAnswer}
                  disabled={isSavingAnswer}
                  onClick={submitForm}
                >
                  {saveButtonLabel}
                </Button>
                <Button
                  type="button"
                  customType="secondary"
                  size="small"
                  disabled={isSavingAnswer}
                  onClick={onCancel}
                >
                  {cancelButtonLabel}
                </Button>
              </div>
            )}
            <AdminAnswerWarningModal
              isOpen={Boolean(pendingAnswer)}
              close={onCloseWarningModal}
              onGoBack={onGoBack}
              onConfirm={onConfirmPendingAnswer}
              isLoading={isSavingAnswer}
            />
          </div>
        );
      }}
    </FormikForm>
  );
};

const IncorporationReadOnlyQuestionAdminField = ({
  question,
  treeId,
  groupId,
  disabled = false,
}: {
  question: Question;
  treeId: string;
  groupId?: string;
  disabled?: boolean;
}) => {
  const { alertToast } = useToast();
  const [uploadDocument, { isLoading: isUploading }] =
    useUploadQuestionDocumentMutation();

  const currentAnswerText =
    question.answer && "text" in question.answer
      ? question.answer.text ?? ""
      : "";
  const savedDocuments =
    question.answer && "documents" in question.answer
      ? question.answer.documents
      : [];

  const [textDraft, setTextDraft] = useState(currentAnswerText);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingRemovalIds, setPendingRemovalIds] = useState<string[]>([]);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const previewModal = useModal();

  const visibleDocuments = savedDocuments.filter(
    (document) => !pendingRemovalIds.includes(document.document_answer_id)
  );
  const isDirty =
    textDraft !== currentAnswerText ||
    pendingFiles.length > 0 ||
    pendingRemovalIds.length > 0;

  const onActionError = (err: unknown) => {
    alertToast({
      message:
        (err as BackendError)?.data?.error?.message ?? "Something went wrong",
    });
  };

  const onTextDraftChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
    setTextDraft(e.target.value);

  const onDropPendingFiles = (files: File[]) =>
    setPendingFiles((prev) => [...prev, ...files]);

  const onRemovePendingFile = (index: number) =>
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));

  const onMarkDocumentForRemoval = (documentAnswerId: string) =>
    setPendingRemovalIds((prev) => [...prev, documentAnswerId]);

  const onCancel = () => {
    setTextDraft(currentAnswerText);
    setPendingFiles([]);
    setPendingRemovalIds([]);
  };

  const onSave = async () => {
    const textChanged = textDraft !== currentAnswerText;
    const hasChanges =
      textChanged || pendingFiles.length > 0 || pendingRemovalIds.length > 0;

    if (!hasChanges || !groupId) {
      return;
    }

    try {
      await uploadDocument({
        groupId,
        questionInstanceId: question.uuid,
        files: pendingFiles,
        text: textChanged ? textDraft : undefined,
        removeDocumentAnswerIds: pendingRemovalIds,
      }).unwrap();

      setPendingFiles([]);
      setPendingRemovalIds([]);
    } catch (err) {
      onActionError(err);
    }
  };

  const isSaving = isUploading;

  const pendingDropFiles: DropFile[] = pendingFiles.map((file) => ({
    uuid: `pending-${file.name}`,
    name: file.name,
    file_type: "DOC",
    is_previewable: false,
  }));
  const savedDropFiles: DropFile[] = visibleDocuments.map((document) => ({
    uuid: document.uuid,
    name: document.name,
    file_type: document.file_type,
    is_previewable: document.is_previewable,
  }));
  const combinedFiles = [...savedDropFiles, ...pendingDropFiles];

  const onDeleteFile = (index: number) => {
    if (index < visibleDocuments.length) {
      onMarkDocumentForRemoval(visibleDocuments[index].document_answer_id);
      return;
    }

    onRemovePendingFile(index - visibleDocuments.length);
  };

  return (
    <FormikForm initialValues={{}} onSubmit={() => {}}>
      <div className="t-flex t-flex-col t-gap-2 t-pt-2">
        <TextArea
          name={`read-only-answer-${question.uuid}`}
          label="Enter updates"
          required
          rows={1}
          size="small"
          value={textDraft}
          onChange={onTextDraftChange}
          disabled={disabled}
        />
        <BaseFileInput
          multiple
          disabled={disabled || !groupId}
          isUploading={isUploading}
          onDrop={onDropPendingFiles}
          onDelete={disabled ? undefined : (_, index) => onDeleteFile(index)}
          onFileClick={({ uuid }) => {
            const isPreviewable = savedDropFiles.some(
              (file) => file.uuid === uuid && file.is_previewable
            );
            if (!isPreviewable) {
              return;
            }

            setPreviewId(uuid);
            previewModal.open();
          }}
          files={combinedFiles}
          label="Upload documents (if any)"
        />
        <Preview
          showModal={previewModal.isOpen}
          closeModal={previewModal.close}
          groupId={groupId}
          previewId={previewId}
        />
        {!disabled && isDirty && (
          <div className="t-flex t-gap-2">
            <Button
              type="button"
              customType="primary"
              size="small"
              isLoading={isSaving}
              disabled={isSaving}
              onClick={onSave}
            >
              Save update
            </Button>
            <Button
              type="button"
              customType="secondary"
              size="small"
              disabled={isSaving}
              onClick={onCancel}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </FormikForm>
  );
};

export const IncorporationQuestionRow = ({
  question,
  source,
  treeId,
  isEditable,
  isAnswerable = false,
  isPublished = false,
  groupId,
}: {
  question: Question;
  source: IncorporationFormSource;
  treeId: string;
  isEditable: boolean;
  isAnswerable?: boolean;
  isPublished?: boolean;
  groupId?: string;
}) => {
  const { isAdmin } = useRoleBasedView();
  const { alertToast } = useToast();
  const [updateAdminStatus] = useUpdateFormInstanceNodeMutation();
  const [isSavingAnswer, setIsSavingAnswer] = useState(false);

  const onAdminStatusChange = async (status: FormInstanceStatus) => {
    try {
      await updateAdminStatus({
        treeId,
        body: { question: { node_id: question.uuid, admin_status: status } },
      }).unwrap();
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  if (isEditable) {
    return (
      <div className="t-border-0 t-border-t t-border-solid t-border-neutral-10 t-py-9">
        <IncorporationQuestionBuilderFields
          question={question}
          source={source}
          treeId={treeId}
          isDisabled={isPublished && isQuestionAnswered(question)}
        />
      </div>
    );
  }

  const isReadOnlyQuestion = question.question_type === QUESTION_TYPE.READ_ONLY;
  const canAdminAnswer = isReadOnlyQuestion && isAdmin && isPublished;
  const isTemplate = source === INCORPORATION_FORM_SOURCE.TEMPLATE;

  let answerContent = (
    <IncorporationQuestionViewOnlyField question={question} />
  );
  if (canAdminAnswer) {
    answerContent = (
      <IncorporationReadOnlyQuestionAdminField
        question={question}
        treeId={treeId}
        groupId={groupId}
        disabled={isTemplate}
      />
    );
  } else if (isAnswerable && !isReadOnlyQuestion) {
    answerContent = (
      <IncorporationQuestionAnswerField
        question={question}
        treeId={treeId}
        groupId={groupId}
        isAdmin={isAdmin}
        onSavingChange={setIsSavingAnswer}
      />
    );
  }

  const helperTextContent = Boolean(question.config?.helper_text) && (
    <span className="t-text-body-sm-italic t-italic t-text-text-30">
      {question.config.helper_text}
    </span>
  );

  return (
    <div className={classNames("t-flex t-flex-col t-gap-1")}>
      <div
        className={classNames(
          "t-flex t-items-center t-justify-between t-gap-4"
        )}
      >
        <span className="t-min-w-0 t-flex-1 ">
          <Label
            required={question.is_mandatory}
            className="t-text-body-sm  t-text-text-60 !t-pb-0"
          >
            {question.label}
          </Label>
        </span>
        {isReadOnlyQuestion &&
          !isTemplate &&
          (canAdminAnswer ? (
            <Dropdown.Root>
              <Dropdown.Trigger asChild>
                <Button type="button" customType="ghost" size="xsmall">
                  <IncorporationAdminStatusDot status={question.status} />
                  <CaretDown className="t-ml-1 t-size-3 t-text-text-30" />
                </Button>
              </Dropdown.Trigger>
              <Dropdown.Portal>
                <Dropdown.Content align="end">
                  <Dropdown.RadioGroup
                    value={String(question.status)}
                    onValueChange={(value) =>
                      void onAdminStatusChange(
                        Number(value) as FormInstanceStatus
                      )
                    }
                  >
                    {ADMIN_STATUS_OPTIONS.map((option) => (
                      <Dropdown.RadioItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </Dropdown.RadioItem>
                    ))}
                  </Dropdown.RadioGroup>
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
          ) : (
            <span className="t-shrink-0">
              <IncorporationAdminStatusDot status={question.status} />
            </span>
          ))}
        {isAnswerable && !isReadOnlyQuestion && (
          <IncorporationQuestionSaveStatus
            isSaving={isSavingAnswer}
            isAnswered={isQuestionAnswered(question)}
          />
        )}
      </div>
      {isReadOnlyQuestion && helperTextContent}
      {answerContent}
      {!isReadOnlyQuestion && helperTextContent}
    </div>
  );
};
